import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../../components/Input';
import Dropdown from '../../../../components/Dropdown';
import { VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';
import { ListFilters } from '../../../../interfaces/developer.interface';
import useDebounce from '../../../../hooks/useDebounce';

interface FilterProps {
  status: any[];
  allStatus: any[];
  states: any[];
  allStates: any[];
  cities: any[];
  allCities: any[];
  dropdownSearchData: any;
  initialFilters: ListFilters;
  onApply: (filters: ListFilters) => void;
  onReset: () => void;
  onDropdownSearchChange: (e: any, name: string) => void;
}

const Filter: React.FC<FilterProps> = ({
  status,
  allStatus,
  states,
  allStates,
  cities,
  allCities,
  dropdownSearchData,
  initialFilters,
  onDropdownSearchChange,
  onApply,
  onReset,
}) => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<ListFilters>(initialFilters);
  const debouncedSearchText = useDebounce(filters.searchText, 500);

  const [filtersApplied, setFiltersApplied] = useState(false);

  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  useEffect(() => {
    const filtersToApply = {
      ...filters,
      searchText: debouncedSearchText
    };

    const applied = Boolean(
      filtersToApply.searchText !== '' ||
      filtersToApply.status !== '1' ||
      filtersToApply.stateId !== '' ||
      filtersToApply.cityId !== ''
    );

    setFiltersApplied(applied);
    onApply(filtersToApply);
  }, [
    debouncedSearchText,
    filters.status,
    filters.stateId,
    filters.cityId,
    onApply
  ]);

  const handleInputChange = useCallback(
    (event: any, name: string) => {
      setFilters((prev: ListFilters) => {
        const newData: any = {
          ...prev,
          [name]: event.target.value,
        };
        // Cascading clear
        if (name === VALIDATION_FIELD_MAPPING.stateId) {
          newData[VALIDATION_FIELD_MAPPING.cityId as keyof ListFilters] = '';
        }
        return newData;
      });
    },
    []
  );

  const handleReset = () => {
    setFilters({
      searchText: '',
      status: '1',
      stateId: '',
      cityId: '',
    });
    onReset();
  };

  return (
    <div className={`card mb-4 ${filtersApplied ? 'border border-primary border-2' : ''}`}>
      <div className="card-body">
        <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
          <h5 className="m-0 text-dark">{t('common.filters')}</h5>
          {filtersApplied && (
            <small
              onClick={handleReset}
              className="cursor-pointer text-primary"
            >
              {t('common.clearFilters')}
            </small>
          )}
        </div>

        <div className="col-12">
          <div className="row g-3">
            <div className="col-md-3 col-sm-12">
              <Input
                type="search"
                id={VALIDATION_FIELD_MAPPING.searchText}
                name={VALIDATION_FIELD_MAPPING.searchText}
                placeholder={t('developers.list.pageTexts.filters.searchPlaceholder')}
                value={filters.searchText}
                onChange={handleInputChange}
                autoComplete="on"
              />
            </div>
              <div className="col-md-3 col-sm-6">
                <Dropdown
                  values={
                    filters.stateId && filters.stateId.toString().trim()
                      ? [String(filters.stateId)]
                      : []
                  }
                  options={(states || []).map((item: any) => ({
                    label: item.name,
                    value: String(item.id),
                  }))}
                  allOptions={(allStates || []).map((item: any) => ({
                    label: item.name,
                    value: String(item.id),
                  }))}
                  id={VALIDATION_FIELD_MAPPING.stateId}
                  name={VALIDATION_FIELD_MAPPING.stateId}
                  searchText={dropdownSearchData.state}
                  onSearch={(e) => onDropdownSearchChange(e, 'state')}
                  placeholder={t('developers.list.pageTexts.filters.selectState')}
                  label={t('developers.list.pageTexts.filters.state')}
                  onChange={handleInputChange}
                />
              </div>
              <div className="col-md-3 col-sm-6">
                <Dropdown
                  values={
                    filters.cityId && filters.cityId.toString().trim()
                      ? [String(filters.cityId)]
                      : []
                  }
                  options={(cities || []).map((item: any) => ({
                    label: item.name,
                    value: String(item.id),
                  }))}
                  allOptions={(allCities || []).map((item: any) => ({
                    label: item.name,
                    value: String(item.id),
                  }))}
                  id={VALIDATION_FIELD_MAPPING.cityId}
                  name={VALIDATION_FIELD_MAPPING.cityId}
                  searchText={dropdownSearchData.city}
                  onSearch={(e) => onDropdownSearchChange(e, 'city')}
                  placeholder={t('developers.list.pageTexts.filters.selectCity')}
                  label={t('developers.list.pageTexts.filters.city')}
                  onChange={handleInputChange}
                />
              </div>
              <div className="col-md-3 col-sm-6">
                <Dropdown
                  values={
                    filters.status && filters.status.toString().trim()
                      ? [String(filters.status)]
                      : []
                  }
                  options={(status || []).map((item: any) => ({
                    label: item.name,
                    value: String(item.id),
                  }))}
                  allOptions={(allStatus || []).map((item: any) => ({
                    label: item.name,
                    value: String(item.id),
                  }))}
                  id={VALIDATION_FIELD_MAPPING.status}
                  name={VALIDATION_FIELD_MAPPING.status}
                  searchText={dropdownSearchData.status}
                  onSearch={(e) => onDropdownSearchChange(e, 'status')}
                  placeholder={t('developers.list.pageTexts.filters.selectStatus')}
                  label={t('developers.list.pageTexts.filters.status')}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default Filter;
